import React, { useEffect, useState } from "react";
import { format, sub } from "date-fns";

// 1. Impor Highcharts dan wrapper React-nya
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// 2. Impor hook dan tipe data yang sama
import useOccupancyMonth from "../../../services/occupancy/hooks/useOccupancyMonth";
import { useAuth } from "../../../auth/context/AuthProvider";
import OccupancyChartSkeleton from "./OccupancyChartSkeleton";

const OccupancyHighchartsComponent: React.FC = () => {
    const { user } = useAuth();
    // State untuk menyimpan konfigurasi chart Highcharts
    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

    // Logika pengambilan data dipertahankan sama persis
    const endDate = format(new Date(), "yyyy-MM-dd");
    const startDate = format(sub(new Date(), { months: 11 }), "yyyy-MM-01");

    const { data: occupancyData, isFetching } = useOccupancyMonth({
        filter: {
            property_id: user?.active_property_id ?? "",
            start: startDate,
            end: endDate,
        },
        enabled: !!user?.active_property_id,
    });

    // useEffect untuk memproses data dan mengatur opsi chart saat data diterima
    useEffect(() => {
        if (occupancyData && occupancyData.data) {
            // 3. Fungsi transformasi data diadaptasi untuk format Highcharts
            const monthLabels: Record<string, string> = {
                "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
                "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
                "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
            };

            const sortedData = [...occupancyData.data].sort((a, b) =>
                a.month.localeCompare(b.month)
            );

            // Pisahkan kategori (sumbu X) dan data series (sumbu Y)
            const categories = sortedData.map((item) => monthLabels[item.month.split("-")[1]]);
            const seriesData = sortedData.map((item) => parseFloat(item.occupancy));

            // 4. Atur semua opsi konfigurasi untuk Highcharts
            setChartOptions({
                chart: {
                    type: 'spline' // Tipe chart adalah 'line'
                },
                title: {
                    text: 'Occupancy Percentage (Last 12 Months)'
                },
                xAxis: {
                    categories: categories,
                    title: {
                        text: "Month"
                    }
                },
                yAxis: {
                    title: {
                        text: "Occupancy Percentage"
                    },
                    min: 0,
                    max: 100,
                    tickInterval: 10 // Ekuivalen dengan 'stepSize' di Chart.js
                },
                tooltip: {
                    // Menambahkan '%' pada nilai di tooltip
                    pointFormat: '{series.name}: <b>{point.y:.2f}%</b>'
                },
                plotOptions: {
                    spline: {
                        marker: {
                            enabled: true,
                            symbol: 'circle',
                            radius: 4
                        }
                    }
                },
                series: [{
                    type: 'spline',
                    name: "Occupancy Percentage",
                    data: seriesData,
                    color: "#007bff"
                }],
                legend: {
                    enabled: true,
                    align: 'center',
                    verticalAlign: 'top'
                },
                credits: {
                    enabled: false
                }
            });
        }
    }, [occupancyData]);

    // 5. Logika render dipertahankan sama, hanya mengganti komponen Line dengan HighchartsReact
    return (
        <>
            {isFetching && <OccupancyChartSkeleton />}
            {!isFetching && chartOptions.series && (
                <div className="mt-4 p-3 bg-white rounded shadow-sm">
                    <div style={{ height: "468px" }}>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartOptions}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default OccupancyHighchartsComponent;